import { styled } from "goober";
import {
  attachedSkillNames,
  stripAttachedSkillsBlock,
} from "../../../shared/skills/attachedSkills";
import { Icon } from "../Icon";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";

export const CodeContextLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  width: fit-content;
  max-width: 200px;
`;

export const AccentBar = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  flex-shrink: 0;

  &::after {
    content: "";
    width: 4px;
    height: 10px;
    border-radius: 2px;
    background: currentColor;
  }
`;

const VoiceLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: 2px;
  height: 12px;
  border-radius: 38px;
  color: var(--text-secondary);
  width: fit-content;
  align-self: flex-end;
`;

type ParsedCodeContext = {
  file: string;
  lines: string;
  color: string;
};

const codeContextPattern =
  /<code-context file="([^"]*)" lines="([^"]*)" color="([^"]*)">\n[\s\S]*?\n<\/code-context>/g;

function parseCodeContext(text: string): {
  snippets: ParsedCodeContext[];
  remainingText: string;
} {
  const snippets: ParsedCodeContext[] = [];
  let remaining = text;
  for (const match of text.matchAll(codeContextPattern)) {
    snippets.push({ file: match[1]!, lines: match[2]!, color: match[3]! });
    remaining = remaining.replace(match[0], "");
  }
  return { snippets, remainingText: remaining.trim() };
}

type ParsedElementContext = {
  step: string;
  element: string;
  color: string;
  index: string;
};

const elementContextPattern =
  /<element-context step="([^"]*)" element="([^"]*)" color="([^"]*)" index="([^"]*)">\n[\s\S]*?\n<\/element-context>/g;

function parseElementContext(text: string): {
  snippets: ParsedElementContext[];
  remainingText: string;
} {
  const snippets: ParsedElementContext[] = [];
  let remaining = text;
  for (const match of text.matchAll(elementContextPattern)) {
    snippets.push({
      step: match[1]!,
      element: match[2]!,
      color: match[3]!,
      index: match[4]!,
    });
    remaining = remaining.replace(match[0], "");
  }
  return { snippets, remainingText: remaining.trim() };
}

function parseAttachedSkills(text: string): {
  skills: string[];
  remainingText: string;
} {
  return {
    skills: attachedSkillNames(text),
    remainingText: stripAttachedSkillsBlock(text),
  };
}

export function MessageContent({
  text,
  isDictated,
  lineClamp,
  actions,
  hideVoice,
  leftBadge,
}: {
  text: string;
  isDictated: boolean;
  lineClamp?: number;
  actions?: React.ReactNode;
  hideVoice?: boolean;
  leftBadge?: React.ReactNode;
}) {
  const { snippets: codeCtx, remainingText: afterCode } =
    parseCodeContext(text);
  const { snippets: elemCtx, remainingText: afterElem } =
    parseElementContext(afterCode);
  const { skills: skillCtx, remainingText } = parseAttachedSkills(afterElem);
  return (
    <>
      {skillCtx.map((name) => (
        <CodeContextLabel key={`skill:${name}`}>
          <Icon name="graduation-cap" size={10} style={{ flexShrink: 0 }} />
          <Text variant="text-small-md" ellipsis>
            {name}
          </Text>
        </CodeContextLabel>
      ))}
      {codeCtx.map((s) => (
        <CodeContextLabel key={`${s.file}:${s.lines}`}>
          <AccentBar style={{ color: `var(--${s.color}-600)` }} />
          <Text variant="text-small-md" ellipsis>
            {s.file}: {s.lines}
          </Text>
        </CodeContextLabel>
      ))}
      {elemCtx.map((s) => (
        <CodeContextLabel key={`${s.step}:${s.element}`}>
          <Icon
            name="mouse-pointer-2"
            size={10}
            color={`var(--${s.color}-600)`}
            style={{ flexShrink: 0 }}
          />
          <Text variant="text-small-md" ellipsis>
            {s.index}
            <MetaSep />
            {s.element}
          </Text>
        </CodeContextLabel>
      ))}
      {remainingText && (
        <Text variant="text-regular">
          <span
            style={{
              whiteSpace: "pre-wrap",
              ...(isDictated && { fontStyle: "italic" }),
              ...(lineClamp != null && {
                display: "-webkit-box",
                WebkitLineClamp: lineClamp,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
            }}
          >
            {isDictated ? `"${remainingText}"` : remainingText}
          </span>
        </Text>
      )}
      {(actions || (isDictated && !hideVoice) || leftBadge) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minHeight: 12,
          }}
        >
          {leftBadge}
          {(actions || (isDictated && !hideVoice)) && (
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {actions}
              {isDictated && !hideVoice && (
                <VoiceLabel style={{ alignSelf: "center" }}>
                  <Icon name="audio-lines" size={10} />
                  <Text variant="text-small-md">Voice</Text>
                </VoiceLabel>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
